import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';


export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The variant ID.
 * @property {HTMLInputElement | undefined} barcode - The variant barcode property used for downstream tracking.
 * @property {HTMLInputElement} variantBarcode - The variant barcode.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} defaultAtcButtons - The default ATC and accelerated checkout wrapper.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement | undefined} notifyMeContainer - The notify me CTA wrapper.
 * @property {HTMLElement | undefined} notifyMeDialogComponent - The notify me dialog component element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #notifyEndpoint = 'https://shop-crm-p.colehaan.com/bis';
  #isVariantLoading = false;

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    this.productSection = document.getElementById(
      "product-information-section"
    );
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener('variant:loading', this.#onVariantLoading, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
     * GA4 add_to_cart tracking
     * @param {any} response - add.js response payload
     * @param {FormData} [formData] - form data used for add, to read quantity
     * @param {DOMStringMap} [dataset] - element dataset for fallbacks (productId/productTitle)
     * @param {string|number} [fallbackVariantId] - variant id used in the request (if response.id missing)
     * @param {object} [ctx]
     * @param {string} [ctx.listName='PDP']
     * @param {number} [ctx.index=0]
     * @param {string} [ctx.source='product-form-component']
     */
    #trackAddToCart(response, formData, dataset, fallbackVariantId, ctx = {}) {
      try {
        if (
          !window.ShopAnalytics ||
          !window.ShopAnalytics.events ||
          typeof window.ShopAnalytics.events.addToCart !== 'function'
        ) {
          return;
        }

        const index = typeof ctx.index === 'number' ? ctx.index : 0;
        const source =
          typeof ctx.source === 'string' ? ctx.source : 'product-form-component';

        const quantity = 1;

        const unitPriceCents =
          typeof response?.final_price === 'number'
            ? response.final_price
            : typeof response?.original_price === 'number'
            ? response.original_price
            : typeof response?.price === 'number'
            ? response.price
            : undefined;

        const product = {
          styleId: response?.properties?.__styleId,
          id: response?.product_id,
          title: response?.product_title || response?.title || dataset?.productTitle,
          vendor: response?.vendor,
          product_type: response?.product_type,
          price: unitPriceCents,
        };

        const variant = {
          id: response?.id || fallbackVariantId,
          title: response?.title,
          sku: response?.sku,
          barcode: response?.properties?._barcode,
          price: unitPriceCents,
        };

        window.ShopAnalytics.events.addToCart({
          product,
          variant,
          quantity,
          index,
          source,
        });
      } catch (err) {
        console.error('GA4 add_to_cart tracking failed', err);
      }
  }

  /**
   * Reads selected variant JSON and returns a safe sku value.
   *
   * @returns {string | undefined}
   */
  #getSelectedVariantSku() {
    const selectedVariantScript =
      this.closest('variant-picker')?.querySelector('#data-selected-variant, script[data-selected-variant]') ||
      document.querySelector('variant-picker #data-selected-variant, variant-picker script[data-selected-variant]');

    const selectedVariantJson = selectedVariantScript?.textContent?.trim();

    if (!selectedVariantJson) return undefined;

    try {
      const selectedVariant = JSON.parse(selectedVariantJson);

      if (!selectedVariant || typeof selectedVariant !== 'object') return undefined;

      const sku = selectedVariant?.sku;

      if (typeof sku === 'string' && sku.trim()) return sku.trim();
      if (typeof sku === 'number') return String(sku);

      return undefined;
    } catch (error) {
      console.warn('Failed to parse selected variant JSON for Bloomreach tracking', error);
      return undefined;
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    const sizeField = document.querySelector('.variant-dropdown[data-option-name="size"]');
    const giftField = document.querySelector('.variant-dropdown[data-option-name="denomination"]');

    
    if (this.classList.contains('sticky-form') && !this.isVariantSelected()) {
      const form = document.querySelector('variant-picker');

      if (form) {
        const rect = form.getBoundingClientRect();
        const offsetTop = rect.top + window.pageYOffset - 140;

        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        });
      }
    }
   
    if (sizeField || giftField) {
      if (!this.isVariantSelected()) return;
    }

    if (this.#isVariantSyncing()) {
      this.#setLiveRegionText('Updating your selected variant. Please wait a moment.');
      return;
    }


    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.hasAttribute('disabled')) return;

    // Bloomreach atc event
    const productId = this.dataset?.styleId;
    const skuId = this.#getSelectedVariantSku();

    if (
      productId &&
      skuId &&
      window.BrTrk &&
      typeof window.BrTrk.getTracker === 'function'
    ) {
      try {
        window.BrTrk.getTracker().logEvent('cart', 'click-add', {
          prod_id: String(productId),
          sku: String(skuId),
        });
      } catch (error) {
        console.warn('Bloomreach add-to-cart tracking failed', error);
      }
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    let eddDate = document.querySelector('.edd-date');

    if (eddDate) {
     formData.append(`attributes[Delivery date]`, eddDate.innerText);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    console.log(fetchCfg, "FETCH CONFIG")

    fetch(Theme.routes.root + 'cart/add.js', {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

           this.#trackAddToCart(response, formData, this.dataset, id, {
              listName: 'PDP',
              index: 0,
              source: 'product-form-component',
            });
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Ensures that a variant is selected from global state.
   * 
   * - If yes - return true.
   * - If not, the element with `data-option-name="size"` is marked
   *   with the `validation-error` class to visually indicate an error.
   *   return false
   *
   * @returns {boolean}
   */
  isVariantSelected() {
    if (this.productSection?.dataset.sizeSelected === "true" || this.productSection?.dataset.giftSelected === "true") {
      return true;
    }

    // No variant param found, highlight the size option group
    const sizeField = document.querySelector('.variant-dropdown[data-option-name="size"]');
    const giftField = document.querySelector('.variant-dropdown[data-option-name="denomination"]');

    if (sizeField ) {
      sizeField.classList.add('validation-error');
    }

    if (giftField) {
      giftField.classList.add('validation-error');
    }

    return false;
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  openNotifyMeModal() {
    this.#setNotifyMeSuccessState(false);
    this.#setNotifyMeStatus('');
    this.refs.notifyMeDialogComponent?.showDialog?.();
  }

  closeNotifyMeModal() {
    this.refs.notifyMeDialogComponent?.closeDialog?.();
  }

  async submitNotifyMe() {
    console.log('[NotifyMe] Submit button clicked');
    const emailInput = this.querySelector('[ref="notifyMeEmail"]');
    const phoneInput = this.querySelector('[ref="notifyMePhone"]');
    const submitButton = this.querySelector('[ref="notifyMeSubmitButton"]');
    const upc = this.refs.barcode?.value?.trim();

    console.log(upc, "UPC BARCODE")

    if (!emailInput) return;

    if (!emailInput.checkValidity()) {
      emailInput.reportValidity();
      return;
    }

    if (!upc) {
      this.#setNotifyMeStatus('Unable to submit right now. Please try again.', true);
      return;
    }

    const payload = {
      email: emailInput.value.trim(),
      phone: phoneInput?.value?.trim() || '',
      upc,
    };

    try {
      if (submitButton) submitButton.disabled = true;
      console.log("ULAZIS U TRY")
      this.#setNotifyMeStatus('Submitting...');
      console.log('[NotifyMe] Submitting request', {
        endpoint: this.#notifyEndpoint,
        payload,
      });

      const response = await fetch(this.#notifyEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      console.log('[NotifyMe] Response received', {
        status: response.status,
        ok: response.ok,
        body: responseText,
      });

      if (!response.ok) {
        throw new Error(`Notify me request failed (${response.status})`);
      }

      this.#setNotifyMeStatus('Thanks. We will notify you when this item is back in stock.');
      this.#setNotifyMeSuccessState(true);
      emailInput.value = '';
      if (phoneInput) phoneInput.value = '';
    } catch (error) {
      console.error('[NotifyMe] Request failed', error);
      this.#setNotifyMeSuccessState(false);
      this.#setNotifyMeStatus('Unable to submit right now. Please try again.', true);
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  }

  /**
   * @param {string} text
   * @param {boolean} [isError]
   */
  #setNotifyMeStatus(text, isError = false) {
    const status = this.querySelector('[ref="notifyMeStatus"]');
    if (!status) return;

    status.textContent = text;
    status.classList.toggle('hidden', !text);
    status.toggleAttribute('data-error', isError);
  }

  /**
   * Toggle success state for notify-me modal content.
   * On success, only status message remains visible.
   * @param {boolean} isSuccess
   */
  #setNotifyMeSuccessState(isSuccess) {
    const content = this.querySelector('.notify-me-dialog__content');
    const status = this.querySelector('[ref="notifyMeStatus"]');

    if (!content || !status) return;

    Array.from(content.children).forEach((child) => {
      if (!(child instanceof HTMLElement)) return;
      if (child === status) return;

      if (isSuccess) {
        child.setAttribute('hidden', 'true');
      } else {
        child.removeAttribute('hidden');
      }
    });
  }

  /**
   * @param {boolean} outOfStock
   * @param {boolean} variantExists
   */
  #toggleNotifyMe(outOfStock, variantExists) {
    if (outOfStock) {
      this.refs.defaultAtcButtons?.setAttribute('hidden', 'true');
      this.refs.notifyMeContainer?.removeAttribute('hidden');
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      return;
    }

    this.refs.notifyMeContainer?.setAttribute('hidden', 'true');
    this.refs.defaultAtcButtons?.removeAttribute('hidden');
    if (variantExists) {
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    } else {
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer, variantBarcode, barcode } = this.refs;
    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');
    if (!currentAddToCartButton) return;

    this.#isVariantLoading = false;
    currentAddToCartButton.removeAttribute('aria-busy');
    delete currentAddToCartButton.dataset.variantLoadingDisabled;

    // Update variant ID early
    variantId.value = event.detail.resource?.id ?? '';
    if (variantBarcode) {
      variantBarcode.value = event.detail.resource?.barcode ?? '';
    }
    if (barcode) {
      barcode.value = event.detail.resource?.barcode ?? '';
    }

    // If you morph, do it first, then reselect
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }
    const btnComp = this.refs.addToCartButtonContainer; // <add-to-cart-component>
    const addToCartButton = btnComp?.refs?.addToCartButton;
    const addToCartLabel = addToCartButton?.querySelector('.add-to-cart-text__content');
    const newAddToCartLabel = newAddToCartButton?.querySelector('.add-to-cart-text__content')?.textContent?.trim();

    if (addToCartLabel && newAddToCartLabel) {
      addToCartLabel.dataset.inStockLabel = newAddToCartLabel;
    }

    const variantExists = Boolean(event.detail.resource);
    const outOfStock = variantExists && event.detail.resource.available === false;

    if (outOfStock) {
      addToCartButton?.setAttribute('disabled', 'true');
      if (addToCartLabel) {
        addToCartLabel.textContent = 'Out of Stock';
      }
    } else {
      if (!variantExists) {
        addToCartButton?.setAttribute('disabled', 'true');
      } else {
        addToCartButton?.removeAttribute('disabled');
      }
      if (addToCartLabel) {
        addToCartLabel.textContent = addToCartLabel.dataset.inStockLabel || addToCartLabel.textContent;
      }
    }
    this.#toggleNotifyMe(outOfStock, variantExists);

    // Update fly-to-cart media (unchanged)
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        btnComp?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * @param {CustomEvent<{ isLoading: boolean }>} event
   */
  #onVariantLoading = (event) => {
    const isLoading = Boolean(event.detail?.isLoading);
    this.#isVariantLoading = isLoading;
    this.#setAddToCartLoadingState(isLoading);
  };

  /**
   * Prevent submits while URL variant and hidden form variant are out of sync.
   * This guards quick clicks before variant picker re-render finishes.
   *
   * @returns {boolean}
   */
  #isVariantSyncing() {
    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    const selectedVariantId = this.refs.variantId?.value || '';

    if (this.#isVariantLoading) return true;
    if (!urlVariantId) return false;

    return String(urlVariantId) !== String(selectedVariantId);
  }

  /**
   * @param {boolean} isLoading
   */
  #setAddToCartLoadingState(isLoading) {
    const addToCartButton = this.refs.addToCartButtonContainer?.refs?.addToCartButton;
    if (!addToCartButton) return;

    if (isLoading) {
      addToCartButton.setAttribute('aria-busy', 'true');
      if (!addToCartButton.hasAttribute('disabled')) {
        addToCartButton.setAttribute('disabled', 'true');
        addToCartButton.dataset.variantLoadingDisabled = 'true';
      }
      return;
    }

    addToCartButton.removeAttribute('aria-busy');
    if (addToCartButton.dataset.variantLoadingDisabled === 'true') {
      addToCartButton.removeAttribute('disabled');
      delete addToCartButton.dataset.variantLoadingDisabled;
    }
  }

}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
